import { useEffect, useState } from "react";
import { list, createRecord, updateRecord, deleteRecord } from "../lib/pb";
import { useT } from "../lib/i18n";
import type { Tag, Article } from "../lib/types";

// 12 preset swatches (bronze + complement palette suited for dark UI).
const PRESET_COLORS = [
  "#b8864a", "#c79256", "#e0a868",
  "#5fa0ff", "#79b1ff",
  "#3ecf71", "#5fcf80",
  "#e05252", "#e07070",
  "#b68fe5", "#e8a8c8", "#9b9b9b",
];

interface Editing {
  id: string | null;
  name: string;
  slug: string;
  color: string;
}

const emptyEditing: Editing = {
  id: null, name: "", slug: "", color: PRESET_COLORS[0],
};

export default function TagsPage() {
  const t = useT();
  const [items, setItems] = useState<Tag[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    setLoading(true);
    try {
      const r = await list<Tag>("tags", { perPage: 200, sort: "name" });
      setItems(r.items);
      // Compute per-tag count via a quick scan of recent articles.
      // Cheap MVP — for big libraries we'd add a server aggregate.
      const ar = await list<Article>("articles", { perPage: 500, sort: "-updated" });
      const c: Record<string, number> = {};
      for (const a of ar.items) {
        for (const id of a.tags || []) c[id] = (c[id] || 0) + 1;
      }
      setCounts(c);
    } catch (e) {
      alert(`Failed to load tags: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  }

  function startNew() { setEditing({ ...emptyEditing }); }
  function startEdit(t: Tag) {
    setEditing({ id: t.id, name: t.name, slug: t.slug, color: t.color || PRESET_COLORS[0] });
  }
  function cancel() { setEditing(null); }

  async function save() {
    if (!editing) return;
    if (!editing.name.trim()) { alert("Name is required"); return; }
    setSaving(true);
    try {
      const data = {
        name: editing.name.trim(),
        slug: editing.slug.trim() || slugify(editing.name),
        color: editing.color,
      };
      if (editing.id) await updateRecord("tags", editing.id, data);
      else await createRecord("tags", data);
      setEditing(null);
      await load();
    } catch (e) {
      alert(`Failed to save: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  async function remove(t: Tag) {
    if (!confirm(`Delete tag "${t.name}"? Articles will lose this tag.`)) return;
    try {
      await deleteRecord("tags", t.id);
      await load();
    } catch (e) {
      alert(`Failed to delete: ${(e as Error).message}`);
    }
  }

  return (
    <div className="page page-narrow">
      <div className="page-header">
        <h1 className="page-title">{t.tagsTitle}</h1>
        <p className="page-subtitle">{t.tagsCount(items.length)}</p>
        <button onClick={startNew} className="btn btn-primary" style={{ marginLeft: "auto" }}>
          {t.tagsAdd}
        </button>
      </div>

      {editing && (
        <div className="card" style={{ padding: 18, marginBottom: 18 }}>
          <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
            <Field label={t.tagsLabelName} style={{ flex: 1 }}>
              <input
                type="text"
                autoFocus
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                onBlur={() => {
                  if (!editing.slug) setEditing({ ...editing, slug: slugify(editing.name) });
                }}
                className="input"
                placeholder={t.tagsNewName}
              />
            </Field>
            <Field label={t.tagsSlug} style={{ flex: 1 }}>
              <input
                type="text"
                value={editing.slug}
                onChange={(e) => setEditing({ ...editing, slug: e.target.value })}
                className="input"
                placeholder="architecture"
              />
            </Field>
          </div>
          <Field label={t.tagsColor}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              {PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setEditing({ ...editing, color: c })}
                  style={{
                    width: 28, height: 28, borderRadius: 100,
                    background: c,
                    border: editing.color === c ? "2px solid var(--text-0)" : "2px solid var(--border-1)",
                    cursor: "pointer", padding: 0,
                  }}
                />
              ))}
              <input
                type="color"
                value={editing.color}
                onChange={(e) => setEditing({ ...editing, color: e.target.value })}
                style={{
                  width: 32, height: 32, background: "transparent",
                  border: "1px solid var(--border-2)", borderRadius: "var(--radius-md)",
                  marginLeft: 4, cursor: "pointer",
                }}
              />
              <input
                type="text"
                value={editing.color}
                onChange={(e) => setEditing({ ...editing, color: e.target.value })}
                className="input mono"
                style={{ width: 110, marginLeft: 4 }}
                placeholder="#b8864a"
              />
            </div>
          </Field>
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button onClick={save} disabled={saving} className="btn btn-primary">
              {saving ? t.tagsSaving : editing.id ? t.save : t.create}
            </button>
            <button onClick={cancel} className="btn btn-secondary">{t.cancel}</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="spinner-line">{t.loading}</div>
      ) : items.length === 0 ? (
        <div className="empty">{t.tagsEmpty}</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {items.map((tag) => (
            <div
              key={tag.id}
              className="card"
              style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px" }}
            >
              <span
                style={{
                  width: 14, height: 14, borderRadius: 100,
                  background: tag.color || PRESET_COLORS[0],
                  flexShrink: 0,
                  boxShadow: "0 0 0 1px var(--border-1)",
                }}
              />
              <span style={{ fontSize: 13, fontWeight: 500, color: "var(--text-0)" }}>
                {tag.name}
              </span>
              <span className="mono" style={{ fontSize: 11, color: "var(--text-3)" }}>
                {tag.slug}
              </span>
              <span
                style={{
                  marginLeft: 8,
                  fontSize: 11,
                  color: "var(--text-3)",
                  padding: "2px 8px",
                  background: "var(--bg-2)",
                  borderRadius: 100,
                }}
                title={t.tagsCount(counts[tag.id] || 0)}
              >
                {counts[tag.id] || 0}
              </span>
              <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                <button onClick={() => startEdit(tag)} className="btn btn-ghost btn-sm">{t.edit}</button>
                <button onClick={() => remove(tag)} className="btn btn-danger btn-sm">{t.tagsDelete}</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({
  label, children, style,
}: {
  label: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <label className="field" style={style}>
      <span className="field-label">{label}</span>
      {children}
    </label>
  );
}

function slugify(s: string): string {
  const map: Record<string, string> = {
    а: "a", б: "b", в: "v", г: "g", д: "d", е: "e", ё: "e", ж: "zh",
    з: "z", и: "i", й: "i", к: "k", л: "l", м: "m", н: "n", о: "o",
    п: "p", р: "r", с: "s", т: "t", у: "u", ф: "f", х: "h", ц: "c",
    ч: "ch", ш: "sh", щ: "sch", ъ: "", ы: "y", ь: "", э: "e", ю: "yu",
    я: "ya",
  };
  return s
    .toLowerCase()
    .split("")
    .map((c) => map[c] ?? c)
    .join("")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}
